import { useCallback, useEffect, useState } from "react";
import { LogOut } from "lucide-react";
import { AdminPovinneRuceni } from "@/components/admin/AdminPovinneRuceni";
import { AdminFixace } from "@/components/admin/AdminFixace";
import { AdminLifeEvents } from "@/components/admin/AdminLifeEvents";
import { AdminVoice } from "@/components/admin/AdminVoice";
import { AdminPasses } from "@/components/admin/AdminPasses";
import { formatApiError, inputCls } from "@/components/admin/shared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pr_admin_token";

const TABS = [
  { key: "povinne", label: "Povinné ručení" },
  { key: "fixace", label: "Fixace hypotéky" },
  { key: "zivotni", label: "Životní události" },
  { key: "vzkazy", label: "Hlasové vzkazy" },
  { key: "pasy", label: "Pojišťovací pasy" },
];

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tab, setTab] = useState("povinne");
  const [login, setLogin] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
  }, []);

  useEffect(() => {
    document.title = "Interní administrace | Daniel Pekař";
    const meta = document.head.querySelector('meta[name="robots"]');
    const prev = meta ? meta.getAttribute("content") : null;
    if (meta) meta.setAttribute("content", "noindex, nofollow");
    return () => {
      document.title = "Daniel Pekař – finanční poradce Ústí nad Labem";
      if (meta && prev) meta.setAttribute("content", prev);
    };
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const res = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(login),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.token) {
        sessionStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
      } else {
        setLoginError(formatApiError(data.detail));
      }
    } catch {
      setLoginError("Přihlášení se nepodařilo. Zkontrolujte připojení.");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <main className="min-h-screen bg-[#FAFAF8] flex items-center justify-center px-6">
        <div className="w-full max-w-md border border-[#F0F0F0] bg-white p-8 md:p-10" data-testid="admin-login-card">
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Interní sekce
          </p>
          <h1 className="font-display text-3xl text-[#252525] mt-4">Přihlášení do administrace</h1>
          <form onSubmit={onLogin} data-testid="admin-login-form" className="mt-8 space-y-6">
            <div>
              <label htmlFor="admin-email" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2">E-mail</label>
              <input
                id="admin-email"
                data-testid="admin-email-input"
                type="email"
                autoComplete="username"
                value={login.email}
                onChange={(e) => setLogin({ ...login, email: e.target.value })}
                className={inputCls}
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs uppercase tracking-widest text-[#737373] font-medium mb-2">Heslo</label>
              <input
                id="admin-password"
                data-testid="admin-password-input"
                type="password"
                autoComplete="current-password"
                value={login.password}
                onChange={(e) => setLogin({ ...login, password: e.target.value })}
                className={inputCls}
              />
            </div>
            {loginError && <p role="alert" data-testid="admin-login-error" className="text-sm text-red-700">{loginError}</p>}
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="w-full border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-8 py-4 text-sm uppercase tracking-widest font-medium disabled:opacity-50"
            >
              {loggingIn ? "Přihlašuji…" : "Přihlásit se"}
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#FAFAF8] py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
              <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
              Interní sekce
            </p>
            <h1 className="font-display text-3xl md:text-4xl text-[#252525] mt-4">Administrace</h1>
          </div>
          <button
            type="button"
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-5 py-3 text-xs uppercase tracking-widest font-medium"
          >
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
            Odhlásit
          </button>
        </div>

        <nav aria-label="Sekce administrace" className="mt-8 border-b border-[#E5E5E5] flex flex-wrap gap-1" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-button-${t.key}`}
              className={`px-5 py-3 text-xs uppercase tracking-widest font-medium transition-colors duration-300 border-b-2 -mb-px ${
                tab === t.key
                  ? "border-[#B69A58] text-[#252525]"
                  : "border-transparent text-[#737373] hover:text-[#252525]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        <div className="mt-10">
          {tab === "povinne" && <AdminPovinneRuceni token={token} logout={logout} />}
          {tab === "fixace" && <AdminFixace token={token} logout={logout} />}
          {tab === "zivotni" && <AdminLifeEvents token={token} logout={logout} />}
          {tab === "vzkazy" && <AdminVoice token={token} logout={logout} />}
          {tab === "pasy" && <AdminPasses token={token} logout={logout} />}
        </div>
      </div>
    </main>
  );
}
